import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import generateId from './generateId';
import { updateHashProps, rewriteUserId } from '../general';
import { addDatasToSave, loadDbModel } from './workBackendSlice';
import { saveAllData, saveData, deleteData, SignUpPayload } from './dataBackend';

export type LatelyCardTemplateDb = {
  userId: string;
  cardTemplateIds: string[];
}
const initial: LatelyCardTemplateDb = {
  userId: '',
  cardTemplateIds: []
};

export type LatelyCardTemplateDbHashType = {
  [key in string]: LatelyCardTemplateDb;
}

export type LatelyCardTemplateAddPayload = {
  id?: string;
} & LatelyCardTemplateDb;
export type LatelyCardTemplateUpdatePayload = {
  id: string;
} & { [P in keyof LatelyCardTemplateDb]?: LatelyCardTemplateDb[P] };

export type LatelyCardTemplateDbState = {
  latelyCardTemplates: LatelyCardTemplateDbHashType;
}

export const generateLatelyCardTemplateId = (): string => generateId('lct');
let initialState: LatelyCardTemplateDbState = { latelyCardTemplates: {} };
const key = 'latelyCardTemplates';

const latelyCardTemplateDbSlice = createSlice({
  name: 'latelyCardTemplateDb',
  initialState,
  reducers: {
    initializeLatelyCardTemplates(state: LatelyCardTemplateDbState) {
      state.latelyCardTemplates = {};
    },
    addLatelyCardTemplate(state: LatelyCardTemplateDbState, action: PayloadAction<LatelyCardTemplateAddPayload>) {
      let { id, ...obj } = action.payload;
      if (!id) id = generateLatelyCardTemplateId();
      state.latelyCardTemplates[id] = obj;
      saveData(key, id, obj);
    },
    updateLatelyCardTemplate(state: LatelyCardTemplateDbState, action: PayloadAction<LatelyCardTemplateUpdatePayload>) {
      const { id, ...obj } = action.payload;
      if (state.latelyCardTemplates[id] === undefined) {
        console.log(`*** LatelyCardTemplates not found: ${id}`);
      } else {
        const newData = { ...state.latelyCardTemplates[id], ...obj };
        state.latelyCardTemplates[id] = newData;
        saveData(key, id, newData);
      }
    },
    deleteLatelyCardTemplate(state: LatelyCardTemplateDbState, action: PayloadAction<LatelyCardTemplateUpdatePayload>) {
      const id = action.payload.id;
      delete state.latelyCardTemplates[id];
      deleteData(key, id);
    },
    saveLatelyCardTemplates(state: LatelyCardTemplateDbState) {
      saveAllData(key, state.latelyCardTemplates);
    },
    signUpLatelyCardTemplates(state: LatelyCardTemplateDbState, action: PayloadAction<SignUpPayload>) {
      const values = rewriteUserId(state.latelyCardTemplates, action.payload);
      addDatasToSave(key, values);
    }
  },
  extraReducers: (builder) => {
    builder.addCase(loadDbModel(key).fulfilled, (state: LatelyCardTemplateDbState, action) => {
      const values = action.payload;
      const [changed, updatedValues] = updateHashProps(initial, values);
      state.latelyCardTemplates = updatedValues;
      if (changed) saveAllData(key, state.latelyCardTemplates);
    })
  }
})

export const { initializeLatelyCardTemplates, addLatelyCardTemplate, updateLatelyCardTemplate, deleteLatelyCardTemplate, saveLatelyCardTemplates, signUpLatelyCardTemplates } = latelyCardTemplateDbSlice.actions;
export default latelyCardTemplateDbSlice.reducer;
